import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Calculator, FileText, Settings, TrendingUp, LogOut, 
  Menu, X, Briefcase, DollarSign, 
  Users, BarChart3, Table2, CheckCircle, GripVertical, Shield, GitCompare, UserCircle, ChevronDown, HelpCircle
} from 'lucide-react';
import api from '@/utils/api';
import NotificationBell from './NotificationBell';
import Dropdown, { DropdownItem, DropdownDivider } from './Dropdown';
import { useToast } from './ToastContainer';
import HelpModal from './HelpModal';


const DEFAULT_MENU_ORDER = [
  'Dashboard', 'Calculator', 'Proposals', 'History', 'Approvals', 'SaaS Agreements',
  'Analytics & Reports', 'Audit Log', 'User Management', 'Data Snapshot', 'Settings'
];

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [menuOrder, setMenuOrder] = useState([]);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [showHelpModal, setShowHelpModal] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || 'preparer';
  const isAdmin = userRole === 'admin';
  const canApprove = ['admin', 'approver', 'cfo', 'ceo'].includes(userRole);
  const canViewAnalytics = ['admin', 'approver', 'cfo', 'ceo', 'account_executive'].includes(userRole);
  const canManageSettings = ['admin'].includes(userRole);
  
  useEffect(() => {
    const loadMenuOrder = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          setMenuOrder(DEFAULT_MENU_ORDER);
          setIsLoadingOrder(false);
          return;
        }
        const response = await api.get('/user/preferences');
        if (response.data.sidebar_menu_order && response.data.sidebar_menu_order.length > 0) {
          setMenuOrder(response.data.sidebar_menu_order);
        } else {
          setMenuOrder(DEFAULT_MENU_ORDER);
        }
      } catch (error) {
        setMenuOrder(DEFAULT_MENU_ORDER);
      } finally {
        setIsLoadingOrder(false);
      }
    };
    loadMenuOrder();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const menuItemsDefinition = [
    { name: 'Dashboard', path: '/dashboard', icon: Home, show: true },
    { name: 'Calculator', path: '/calculator', icon: Calculator, show: true },
    { name: 'Proposals', path: '/proposals', icon: FileText, show: true },
    { name: 'History', path: '/calculation-history', icon: TrendingUp, show: true },
    { name: 'Approvals', path: '/approvals', icon: CheckCircle, show: true }, // All users can view
    { name: 'SaaS Agreements', path: '/agreements', icon: Briefcase, show: true }, // All users can access
    { name: 'Analytics & Reports', path: '/analytics', icon: BarChart3, show: canViewAnalytics },
    { name: 'Compare', path: '/compare', icon: GitCompare, show: true }, // Available to ALL users
    { name: 'User Management', path: '/users', icon: Users, show: isAdmin },
    { name: 'Audit Log', path: '/audit-log', icon: Shield, show: isAdmin },
    { name: 'Data Snapshot', path: '/data-snapshot', icon: Shield, show: isAdmin },
    { name: 'Settings', path: '/settings-manager', icon: Settings, show: canManageSettings },
    { name: 'Fee Table Analytics', path: '/fee-table-calculator', icon: Table2, show: false }, // Hidden - route still accessible via direct URL
  ];

  const menuItemsMap = {};
  menuItemsDefinition.forEach(item => { menuItemsMap[item.name] = item; });

  // Build ordered menu items, ensuring Dashboard is ALWAYS first
  const orderedItems = menuOrder.map(name => menuItemsMap[name]).filter(item => item && item.show);
  const orderedItemNames = new Set(orderedItems.map(item => item.name));
  const missingItems = menuItemsDefinition.filter(item => item.show && !orderedItemNames.has(item.name));
  
  // Combine and ensure Dashboard is first
  let menuItems = [...orderedItems, ...missingItems];
  const dashboardIndex = menuItems.findIndex(item => item.name === 'Dashboard');
  if (dashboardIndex > 0) {
    const dashboard = menuItems.splice(dashboardIndex, 1)[0];
    menuItems.unshift(dashboard);
  }

  const handleDragStart = (e, itemName) => {
    // Prevent dragging Dashboard (always keep it first)
    if (itemName === 'Dashboard') {
      e.preventDefault();
      return;
    }
    setDraggedItem(itemName);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.currentTarget);
  };

  const handleDragOver = (e, itemName) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || draggedItem === itemName || itemName === 'Dashboard') {
      return;
    }
    
    setDragOverItem(itemName);
  };

  const handleDrop = (e, dropTargetName) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!draggedItem || draggedItem === dropTargetName || dropTargetName === 'Dashboard') {
      return;
    }
    
    // Reorder the menu
    const newOrder = [...menuOrder];
    const draggedIndex = newOrder.indexOf(draggedItem);
    const targetIndex = newOrder.indexOf(dropTargetName);
    
    if (draggedIndex === -1 || targetIndex === -1) return;
    
    // Remove dragged item
    newOrder.splice(draggedIndex, 1);
    // Insert at new position
    newOrder.splice(targetIndex, 0, draggedItem);
    
    setMenuOrder(newOrder);
    setDragOverItem(null);
  };

  const handleDragEnd = async () => {
    if (draggedItem !== null) {
      try {
        const token = localStorage.getItem('access_token');
        if (token) {
          await api.post('/user/preferences/sidebar-order', { sidebar_menu_order: menuOrder });
          toast.success('Menu order saved');
        }
      } catch (error) {
        console.error('Failed to save menu order:', error);
        localStorage.setItem('sidebar_menu_order', JSON.stringify(menuOrder));
        toast.info('Menu order saved locally');
      }
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverItem(null);
  };

  const NavItem = ({ item, index }) => {
    const isActive = location.pathname === item.path || 
                     location.pathname === item.path.split('?')[0];
    const isDragging = draggedItem === item.name;
    const isDropTarget = dragOverItem === item.name;
    const isDashboard = item.name === 'Dashboard';

    return (
      <div
        draggable={!isDashboard}
        onDragStart={(e) => handleDragStart(e, item.name)}
        onDragOver={(e) => handleDragOver(e, item.name)}
        onDrop={(e) => handleDrop(e, item.name)}
        onDragEnd={handleDragEnd}
        onDragLeave={handleDragLeave}
        className={`relative group ${isDragging ? 'opacity-50' : ''} ${isDropTarget ? 'scale-105' : ''} transition-all duration-200`}
      >
        {isDropTarget && (
          <div className="absolute inset-0 bg-sky-500/20 border-2 border-sky-400 rounded-lg pointer-events-none" />
        )}
        
        <Link
          to={item.path}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${item.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 mb-0.5 text-[13px] ${
            isActive
              ? 'bg-white/[0.12] text-white font-semibold shadow-sm shadow-black/10'
              : 'text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'
          }`}
        >
          <GripVertical 
            className={`w-3.5 h-3.5 transition-opacity flex-shrink-0 ${
              isDashboard 
                ? 'opacity-0' 
                : 'opacity-0 group-hover:opacity-50 cursor-move'
            }`} 
          />
          <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-sky-400' : ''}`} />
          <span className="truncate">{item.name}</span>
          {isActive && <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-sky-400 rounded-r-full" />}
        </Link>
      </div>
    );
  };

  return (
    <>
      {/* Mobile Toggle */}
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
      >
        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <div className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-60 bg-slate-900 border-r border-slate-800
        transform transition-transform duration-300 ease-in-out
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo/Brand */}
          <div className="px-5 pt-6 pb-5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-sky-500/20 flex items-center justify-center">
                <DollarSign className="w-4.5 h-4.5 text-sky-400" />
              </div>
              <div>
                <h1 className="text-[15px] font-bold text-white tracking-tight leading-tight">
                  GW Pricing
                </h1>
                <p className="text-[10px] text-slate-500 font-medium tracking-wide uppercase">Calculator Tool</p>
              </div>
            </div>
          </div>

          <div className="mx-4 mb-3 border-t border-slate-800/80" />

          {/* Drag hint */}
          {!isLoadingOrder && menuItems.length > 0 && (
            <div className="px-3 mb-2">
              <div className="text-[10px] text-slate-500 px-2 py-1 rounded bg-slate-800/50 flex items-center gap-1.5">
                <GripVertical className="w-3 h-3" />
                <span>Drag items to reorder (except Dashboard)</span>
              </div>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 px-3 overflow-y-auto">{isLoadingOrder ? (
              <div className="text-slate-500 text-center py-4 text-sm">Loading menu...</div>
            ) : (
              menuItems.map((item, index) => (
                <NavItem key={item.name} item={item} index={index} />
              ))
            )}
          </nav>

          {/* Notification Bell + User Profile */}
          <div className="px-3 mb-2">
            <div className="flex items-center justify-between px-3 py-1.5 mb-1">
              <span className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Alerts</span>
              <NotificationBell />
            </div>
          </div>
          <div className="p-3 mx-3 mb-3 rounded-lg bg-slate-800/60">
            <div className="flex items-center gap-2.5 mb-2.5">
              <div className="w-8 h-8 bg-sky-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-sky-400 font-semibold text-xs">
                  {user.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-medium text-slate-200 truncate">
                  {user.name || 'User'}
                </p>
                <p className="text-[11px] text-slate-500 truncate">
                  {user.email}
                </p>
              </div>
            </div>
            <div className="space-y-1">
              <button
                onClick={() => navigate('/profile')}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 rounded-md transition-colors text-slate-400 hover:text-slate-200"
              >
                <UserCircle className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">My Profile</span>
              </button>
              <button
                onClick={() => setShowHelpModal(true)}
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 rounded-md transition-colors text-slate-400 hover:text-slate-200"
              >
                <HelpCircle className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Help Center</span>
              </button>
              <button
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full flex items-center justify-center gap-2 px-3 py-1.5 bg-slate-700/60 hover:bg-slate-700 rounded-md transition-colors text-slate-400 hover:text-slate-200"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="text-xs font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}
      
      {/* Help Modal */}
      <HelpModal show={showHelpModal} onClose={() => setShowHelpModal(false)} />
    </>
  );
}
